import { useState, useRef, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import type { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ASPECT = 4 / 3;
const OUTPUT_WIDTH = 1600;
const OUTPUT_HEIGHT = 1200;
const JPEG_QUALITY = 0.6;

function centerAspectCrop(w: number, h: number): Crop {
  return centerCrop(makeAspectCrop({ unit: '%', width: 90 }, ASPECT, w, h), w, h);
}

async function getCroppedBlob(image: HTMLImageElement, crop: PixelCrop): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;
  const ctx = canvas.getContext('2d')!;
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  ctx.drawImage(
    image,
    crop.x * scaleX, crop.y * scaleY,
    crop.width * scaleX, crop.height * scaleY,
    0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT,
  );
  return new Promise((resolve, reject) =>
    canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/jpeg', JPEG_QUALITY)
  );
}

interface Props {
  file: File;
  onConfirm: (blob: Blob, filename: string) => void;
  onCancel: () => void;
}

export default function ImageCropModal({ file, onConfirm, onCancel }: Props) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [processing, setProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const srcUrl = useRef(URL.createObjectURL(file)).current;

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height));
  }, []);

  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop) return;
    setProcessing(true);
    try {
      const blob = await getCroppedBlob(imgRef.current, completedCrop);
      onConfirm(blob, file.name.replace(/\.[^/.]+$/, '') + '.jpg');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div style={{ background: '#fff', borderRadius: '0.75rem', boxShadow: '0 25px 50px rgba(0,0,0,0.3)', width: '100%', maxWidth: '680px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
          <div>
            <p style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827', margin: 0 }}>Beskær billede</p>
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>4:3 format — gemmes som 1600×1200 px JPEG</p>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Crop area */}
        <div style={{ padding: '1.5rem', background: '#f9fafb', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ReactCrop
            crop={crop}
            onChange={c => setCrop(c)}
            onComplete={c => setCompletedCrop(c)}
            aspect={ASPECT}
            minWidth={50}
            keepSelection
          >
            <img
              ref={imgRef}
              src={srcUrl}
              alt="Crop"
              onLoad={onImageLoad}
              style={{ maxHeight: '52vh', maxWidth: '100%', display: 'block' }}
            />
          </ReactCrop>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}>
          <button onClick={onCancel}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, color: '#374151', background: '#f3f4f6', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>
            Annuller
          </button>
          <button onClick={handleConfirm} disabled={!completedCrop || processing}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, color: '#fff', background: completedCrop && !processing ? '#2563eb' : '#93c5fd', border: 'none', borderRadius: '0.5rem', cursor: completedCrop ? 'pointer' : 'not-allowed' }}>
            {processing ? 'Behandler...' : 'Brug dette billede'}
          </button>
        </div>
      </div>
    </div>
  );
}
